import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { getBackendURL, apiFetch } from '../../../utils/utils';

const AVAILABLE_ROUTES = [
  { key: 'home', label: 'Home / Dashboard' },
  { key: 'digi-card-sites', label: 'Digi Card — Sites' },
  { key: 'digi-card-templates', label: 'Digi Card — Templates' },
  { key: 'distributors', label: 'Distributors' },
  { key: 'distributors-add', label: 'Add Distributor' },
  { key: 'distributors-plans', label: 'Distributor Plans' },
  { key: 'distributors-payments', label: 'Distributor Payments' },
  { key: 'merchants', label: 'Merchants' },
  { key: 'merchants-add', label: 'Add Merchant' },
  { key: 'merchants-plans', label: 'Merchant Plans' },
  { key: 'merchants-payments', label: 'Merchant Payments' },
  { key: 'settings', label: 'Settings' },
];

function SupportUsersPage() {
  const { isOpen: isStatusOpen, onOpen: onStatusOpen, onClose: onStatusClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isFeaturesOpen, onOpen: onFeaturesOpen, onClose: onFeaturesClose } = useDisclosure();
  const cancelRef = useRef();

  const [supportUsers, setSupportUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRoutes, setEditRoutes] = useState([]);
  const [editSitesCount, setEditSitesCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSupportUsers = useCallback(() => {
    setIsLoading(true);
    apiFetch(
      `${getBackendURL()}/admin/support/list?search=${search}&page=${page}&limit=${limit}`
    )
      .then(response => response.json())
      .then(response => {
        if (response.supportUsers && response.supportUsers.length > 0) {
          setSupportUsers(response.supportUsers);
          setTotal(response.total);
        } else {
          setSupportUsers([]);
          setTotal(0);
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setSupportUsers([]);
        setTotal(0);
        setIsLoading(false);
      });
  }, [search, page, limit]);

  useEffect(() => {
    fetchSupportUsers();
  }, [fetchSupportUsers]);

  const onToggleStatus = (user) => {
    setSelectedUser(user);
    setError('');
    setSuccess('');
    setInProgress(false);
    onStatusOpen();
  };

  const toggleStatus = () => {
    if (!selectedUser) return;
    setInProgress(true);
    setError('');
    apiFetch(`${getBackendURL()}/admin/support/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: selectedUser._id, isActive: !selectedUser.isActive }),
    })
      .then(res => res.json())
      .then(res => {
        setInProgress(false);
        if (res.updated) {
          setSuccess(res.message);
          fetchSupportUsers();
          setTimeout(() => onStatusClose(), 1000);
        } else {
          setError(res.message);
        }
      })
      .catch(err => {
        setInProgress(false);
        setError(err.toString());
      });
  };

  const onEditUser = (user) => {
    setSelectedUser(user);
    setEditRoutes(user.assignedRoutes || []);
    setEditSitesCount(user.allowedSitesCount || 0);
    setError('');
    setSuccess('');
    setInProgress(false);
    onEditOpen();
  };

  const updateUser = () => {
    if (!selectedUser) return;
    setInProgress(true);
    setError('');
    apiFetch(`${getBackendURL()}/admin/support/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: selectedUser._id,
        assignedRoutes: editRoutes,
        allowedSitesCount: editSitesCount,
      }),
    })
      .then(res => res.json())
      .then(res => {
        setInProgress(false);
        if (res.updated) {
          setSuccess(res.message);
          fetchSupportUsers();
          setTimeout(() => onEditClose(), 1000);
        } else {
          setError(res.message);
        }
      })
      .catch(err => {
        setInProgress(false);
        setError(err.toString());
      });
  };

  const onDeleteUser = (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) return;
    apiFetch(`${getBackendURL()}/admin/support/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: user._id }),
    })
      .then(res => res.json())
      .then(res => {
        if (res.deleted) {
          fetchSupportUsers();
        }
      })
      .catch(err => console.error(err));
  };

  const handleRouteCheckChange = (routeKey) => {
    setEditRoutes(prev =>
      prev.includes(routeKey) ? prev.filter(r => r !== routeKey) : [...prev, routeKey]
    );
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">Home</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/support-users">Support Users</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4}>
        <Flex flex={1}>
          <Box w={'100%'}>
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Flex spacing="4" alignItems="center" flexWrap="wrap">
                  <Heading size="md" mb={{ base: 2, md: 0 }}>
                    Support Users
                  </Heading>
                  <Input
                    placeholder="Search name or email..."
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    mx={{ base: 0, md: 4 }}
                    maxW="300px"
                    size="sm"
                  />
                </Flex>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer data-testid="support-users-table">
                  {isLoading ? (
                    <Flex justify="center" align="center" minH="200px">
                      <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
                    </Flex>
                  ) : (
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Sr. No</Th>
                          <Th>Name</Th>
                          <Th>Email</Th>
                          <Th>Status</Th>
                          <Th>Assigned features</Th>
                          <Th>Sites (Used/Allowed)</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {supportUsers.map((su, index) => (
                          <Tr key={su._id}>
                            <Td>{(page - 1) * limit + index + 1}</Td>
                            <Td>{su.displayName || su.email.split('@')[0]}</Td>
                            <Td>{su.email}</Td>
                            <Td>
                              <Badge colorScheme={su.isActive ? 'green' : 'red'}>
                                {su.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </Td>
                            <Td>
                              {su.assignedRoutes && su.assignedRoutes.length > 0 ? (
                                <Button 
                                  size="sm" 
                                  variant="link" 
                                  colorScheme="blue" 
                                  onClick={() => { setSelectedUser(su); onFeaturesOpen(); }}
                                >
                                  View Features ({su.assignedRoutes.length})
                                </Button>
                              ) : (
                                <Text fontSize="sm">None</Text>
                              )}
                            </Td>
                            <Td>{su.usedSitesCount || 0} / {su.allowedSitesCount || 0}</Td>
                            <Td>
                              <Stack direction={{ base: 'column', lg: 'row' }} spacing={2}>
                                <Button
                                  colorScheme="blue"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onEditUser(su)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  colorScheme={su.isActive ? 'red' : 'green'}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onToggleStatus(su)}
                                >
                                  {su.isActive ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button
                                  colorScheme="red"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onDeleteUser(su)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </TableContainer>
              </CardBody>
              <Divider />
              <Flex p={4} justify="space-between" align="center">
                <Text fontSize="sm" color="gray.500">
                  Showing {total > 0 ? (page - 1) * limit + 1 : 0} to{' '}
                  {Math.min(page * limit, total)} of {total} results
                </Text>
                <Flex gap={2}>
                  <Button size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
                    Previous
                  </Button>
                  <Button size="sm" onClick={() => setPage(page + 1)} disabled={page * limit >= total}>
                    Next
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Box>
        </Flex>
      </Stack>

      {/* Toggle Status Dialog */}
      <AlertDialog isOpen={isStatusOpen} leastDestructiveRef={cancelRef} onClose={onStatusClose} closeOnOverlayClick={false}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Update Status
            </AlertDialogHeader>
            <AlertDialogBody>
              {success && <Alert status="success" mb={3}><AlertIcon />{success}</Alert>}
              {error && <Alert status="error" mb={3}><AlertIcon />{error}</Alert>}
              {!success && !error && (
                <Text>
                  Are you sure you want to {selectedUser?.isActive ? 'deactivate' : 'activate'}{' '}
                  <strong>{selectedUser?.email}</strong>?
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onStatusClose}>Cancel</Button>
              {!success && (
                <Button
                  colorScheme={selectedUser?.isActive ? 'red' : 'green'}
                  onClick={toggleStatus}
                  ml={3}
                  isLoading={inProgress}
                >
                  {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit User Dialog */}
      <AlertDialog isOpen={isEditOpen} leastDestructiveRef={cancelRef} onClose={onEditClose} closeOnOverlayClick={false} size="xl">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Edit Support User — {selectedUser?.email}
            </AlertDialogHeader>
            <AlertDialogBody>
              {success && <Alert status="success" mb={3}><AlertIcon />{success}</Alert>}
              {error && <Alert status="error" mb={3}><AlertIcon />{error}</Alert>}
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel>Assigned features</FormLabel>
                  <CheckboxGroup>
                    <Stack spacing={2}>
                      {AVAILABLE_ROUTES.map(route => (
                        <Checkbox
                          key={route.key}
                          isChecked={editRoutes.includes(route.key)}
                          onChange={() => handleRouteCheckChange(route.key)}
                        >
                          {route.label}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>
                <FormControl>
                  <FormLabel>Allowed Sites Count</FormLabel>
                  <NumberInput
                    min={0}
                    value={editSitesCount}
                    onChange={(_, val) => setEditSitesCount(val)}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Stack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onEditClose}>Cancel</Button>
              {!success && (
                <Button colorScheme="teal" onClick={updateUser} ml={3} isLoading={inProgress}>
                  Update
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* View Features Dialog */}
      <AlertDialog isOpen={isFeaturesOpen} leastDestructiveRef={cancelRef} onClose={onFeaturesClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Assigned features
            </AlertDialogHeader>
            <AlertDialogBody>
              <Stack spacing={2}>
                {selectedUser?.assignedRoutes?.map(routeKey => {
                  const routeLabel = AVAILABLE_ROUTES.find(r => r.key === routeKey)?.label || routeKey;
                  return <Text key={routeKey}>• {routeLabel}</Text>;
                })}
              </Stack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onFeaturesClose}>Close</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default SupportUsersPage;
